import { useCallback, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { endpoints } from '@rossum/api-client';
import AnnotationsActionModal from '../../../../../components/AnnotationsActionModal';
import { notify } from '../../../../../features/toaster';
import { api } from '../../../../../lib/apiClient';
import { handleTooManyImportsError } from '../../../../../lib/errorHandlers';
import { displayAnnotation } from '../../../../../redux/modules/annotation/actions';
import { isReAutomatable } from '../../../../../redux/modules/annotation/helpers';
import { userSelector } from '../../../../../redux/modules/user/selectors';
import { Annotation } from '../../../../../types/annotation';
import { SidebarFooterAction } from './utils';

export const useReAutomateAction = (
  annotation: Annotation
): { action: SidebarFooterAction | null; modal: JSX.Element | null } => {
  const intl = useIntl();

  const { status, modifier, restrictedAccess } = annotation;

  const currentUserUrl = useSelector(userSelector).url;

  const dispatch = useDispatch();

  const { mutate: reAutomateAnnotation } = useMutation({
    mutationFn: () =>
      api.request(
        endpoints.internal.reautomate({
          annotations: [annotation.url],
        })
      ),
    onSuccess: () => {
      notify.default({
        title: intl.formatMessage(
          {
            id: 'components.annotationOverview.actionButtons.reAutomate.scheduled',
          },
          { count: 1 }
        ),
      });
      dispatch(displayAnnotation({ id: annotation.id, mode: 'refresh' }));
    },
    onError: (error: unknown) => {
      try {
        handleTooManyImportsError(error, intl);
      } catch {
        notify.error({
          title: intl.formatMessage({
            id: 'components.annotationOverview.actionButtons.reAutomate.error',
          }),
        });
      }
    },
  });

  const [modalOpen, setModalOpen] = useState(false);

  const disabled =
    restrictedAccess || !isReAutomatable({ status, modifier, currentUserUrl });

  const handleConfirm = useCallback(() => {
    setModalOpen(false);
    reAutomateAnnotation();
  }, [reAutomateAnnotation]);

  const handleClick = useCallback(() => {
    setModalOpen(true);
  }, []);

  const result = useMemo(() => {
    return {
      modal: modalOpen ? (
        <AnnotationsActionModal
          action="reAutomate"
          annotationsLength={1}
          onClose={() => setModalOpen(false)}
          onPrimaryActionClick={handleConfirm}
          dataCyConfirm="reautomate-dialog-confirm-btn"
          dataCyCancel="reautomate-dialog-cancel-btn"
        />
      ) : null,
      action: {
        id: 'reAutomate' as const,
        label: intl.formatMessage({
          id: 'containers.annotationList.selectionPanel.buttons.reAutomate',
        }),
        disabled,
        onClick: handleClick,
        dataCy: 'annotation-sidebar-reautomate',
      },
    };
  }, [intl, disabled, handleClick, modalOpen, handleConfirm]);

  return result;
};
